import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { mergeMap, Observable, of, throwError, timer } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Client, PageResponse } from '../models/models';

export interface ClientPhotoUploadJobStatusDto {
  jobId: number;
  status: string;
  fileName?: string | null;
  errorMessage?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ClientService {
  private readonly apiUrl = `${environment.apiUrl}/clients`;

  constructor(private http: HttpClient) {}

  getClients(search: string = '', page: number = 0, size: number = 10, sort: string = 'denomination,asc', showArchived: boolean = false): Observable<PageResponse<Client>> {
    let params = new HttpParams()
      .set('search', search)
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('showArchived', showArchived.toString());
    return this.http.get<PageResponse<Client>>(this.apiUrl, { params });
  }

  createClient(client: Client): Observable<any> {
    return this.http.post(this.apiUrl, client);
  }

  uploadPhoto(id: number, photo: File): Observable<any> {
    const formData = new FormData();
    formData.append('photo', photo);
    return this.http.post(`${this.apiUrl}/${id}/photo`, formData);
  }

  uploadPhotoAsync(id: number, photo: File): Observable<ClientPhotoUploadJobStatusDto> {
    const formData = new FormData();
    formData.append('photo', photo);
    return this.http.post<ClientPhotoUploadJobStatusDto>(`${this.apiUrl}/${id}/photo/async`, formData);
  }

  getPhotoUploadStatus(id: number, jobId: number): Observable<ClientPhotoUploadJobStatusDto> {
    return this.http.get<ClientPhotoUploadJobStatusDto>(`${this.apiUrl}/${id}/photo-upload-jobs/${jobId}`);
  }

  waitForPhotoUpload(id: number, jobId: number, intervalMs: number = 1000): Observable<ClientPhotoUploadJobStatusDto> {
    return timer(0, intervalMs).pipe(
      switchMap(() => this.getPhotoUploadStatus(id, jobId)),
      filter(status => status.status === 'SUCCESS' || status.status === 'FAILED'),
      take(1),
      mergeMap((status) => {
        if (status.status === 'SUCCESS') {
          return of(status);
        }
        return throwError(() => new Error(status.errorMessage || 'Erreur lors de l\'upload de la photo.'));
      })
    );
  }

  getPhotoUrl(id: number, cacheKey?: string | number): string {
    const suffix = cacheKey !== undefined ? `?v=${encodeURIComponent(String(cacheKey))}` : '';
    return `${this.apiUrl}/${id}/photo${suffix}`;
  }

  getPhotoBlob(id: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${id}/photo`, {
      responseType: 'blob'
    });
  }

  getClientById(id: number): Observable<Client> {
    return this.http.get<Client>(`${this.apiUrl}/${id}`);
  }

  updateClient(id: number, client: Client): Observable<any> {
    return this.http.put(`${this.apiUrl}/${id}`, client);
  }

  deleteClient(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`);
  }

  canDeleteClient(id: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/${id}/can-delete`);
  }
}
